/**
 * RFQ Detail Page (Solicitud de Cotización)
 *
 * Shows an RFQ header (número, proyecto, cliente, estado) and its line items
 * grouped by `alcance`. Actions: Descargar PDF, Editar, Marcar enviada
 * (disabled once status === 'sent'). Inferred items are flagged so the silence
 * bug (#356) never happens — the line is shown, just marked.
 *
 * @module pages/rfq/RfqDetailPage
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import {
  getRfq,
  markRfqSent,
  RFQ_STATUS,
  RFQ_STATUS_LABELS,
  RFQ_STATUS_VARIANTS,
} from '../../services/rfqService';
import { exportRfqToPDF } from '../../utils/rfqExport';
import { toast } from '../../utils/toast';
import { formatDate } from '../../utils/formatters';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Group items by `alcance`, preserving first-seen order. Items without an
 * alcance fall into a "Sin alcance" bucket so nothing is silently dropped.
 */
const groupByAlcance = (items = []) => {
  const groups = [];
  const index = new Map();
  items.forEach((item) => {
    const key = item.alcance || 'Sin alcance';
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ alcance: key, items: [] });
    }
    groups[index.get(key)].items.push(item);
  });
  return groups;
};

const RfqDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [downloading, setDownloading] = useState(false);

  const {
    data: rfqResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['rfq', id],
    queryFn: () => getRfq(id),
    enabled: !!id,
  });

  const rfq = rfqResponse?.data;

  const markSentMutation = useMutation({
    mutationFn: () => markRfqSent(id),
    onSuccess: (res) => {
      if (res?.error) {
        toast.error('Error al marcar como enviada: ' + (res.error.message || ''));
        return;
      }
      toast.success('Solicitud marcada como enviada');
      queryClient.invalidateQueries({ queryKey: ['rfq', id] });
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
    },
    onError: (err) => {
      toast.error('Error al marcar como enviada: ' + err.message);
    },
  });

  const handleDownload = async () => {
    if (!rfq) return;
    setDownloading(true);
    try {
      await exportRfqToPDF(rfq);
    } catch (err) {
      console.error('Download RFQ PDF error:', err);
      toast.error('Error al generar el PDF: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Container fluid className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Cargando solicitud...</p>
      </Container>
    );
  }

  if (error || !rfq) {
    return (
      <Container fluid>
        <Alert variant="danger">
          {error?.message || 'No se pudo cargar la solicitud'}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/solicitudes-cotizacion')}>
          <i className="bi bi-arrow-left me-2"></i>
          Volver
        </Button>
      </Container>
    );
  }

  // #422: con proyecto, el cliente "oficial" es project.client; sin
  // proyecto (compra por excepción), cae al cliente directo rfqs.client_id.
  const client = rfq.project?.client || rfq.client || {};
  const groups = groupByAlcance(rfq.items || []);
  const isSent = rfq.status === RFQ_STATUS.SENT;

  return (
    <DashboardLayout>
    <Container fluid className="rfq-detail">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2 text-decoration-none"
            onClick={() => navigate('/solicitudes-cotizacion')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Volver a solicitudes
          </Button>
          <h2 className="mb-1">
            <i className="bi bi-file-earmark-ruled me-2"></i>
            {rfq.rfq_number || '(Sin número)'}
          </h2>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={RFQ_STATUS_VARIANTS[rfq.status] || 'secondary'}>
              {RFQ_STATUS_LABELS[rfq.status] || rfq.status}
            </Badge>
            <span className="text-muted small">{formatDate(rfq.created_at)}</span>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="outline-primary"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Generando...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-arrow-down me-2"></i>
                Descargar PDF
              </>
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate(`/solicitudes-cotizacion/${id}/editar`)}
          >
            <i className="bi bi-pencil me-2"></i>
            Editar
          </Button>
          <Button
            variant="success"
            onClick={() => markSentMutation.mutate()}
            disabled={isSent || markSentMutation.isPending}
            title={isSent ? 'Esta solicitud ya fue enviada' : 'Marcar como enviada'}
          >
            {markSentMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Procesando...
              </>
            ) : (
              <>
                <i className="bi bi-send me-2"></i>
                {isSent ? 'Enviada' : 'Marcar enviada'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Header info */}
      <Row className="g-3 mb-4">
        <Col xs={12} md={6}>
          <Card className="shadow-sm h-100">
            <Card.Header>
              <strong>
                <i className="bi bi-folder me-2"></i>
                Proyecto
              </strong>
            </Card.Header>
            <Card.Body>
              {rfq.project ? (
                <>
                  <div className="fw-semibold">{rfq.project.project_number || '-'}</div>
                  {rfq.project.title && (
                    <div className="text-muted">{rfq.project.title}</div>
                  )}
                </>
              ) : (
                <div className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Sin proyecto (compra por excepción)
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm h-100">
            <Card.Header>
              <strong>
                <i className="bi bi-building me-2"></i>
                Cliente
              </strong>
            </Card.Header>
            <Card.Body>
              <div className="fw-semibold">{client.company_name || '-'}</div>
              {client.legal_id && (
                <div className="text-muted small">Cédula: {client.legal_id}</div>
              )}
              {client.primary_contact_email && (
                <div className="text-muted small">{client.primary_contact_email}</div>
              )}
              {client.primary_contact_phone && (
                <div className="text-muted small">{client.primary_contact_phone}</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {rfq.title && (
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <div className="text-muted small">Título</div>
            <div className="fw-semibold">{rfq.title}</div>
          </Card.Body>
        </Card>
      )}

      {/* Items grouped by alcance */}
      <Card className="shadow-sm mb-4">
        <Card.Header>
          <strong>
            <i className="bi bi-list-ul me-2"></i>
            Ítems solicitados
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {groups.length === 0 ? (
            <div className="text-center py-4 text-muted">
              Esta solicitud no tiene ítems.
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.alcance} className="rfq-alcance-group">
                <div className="px-3 py-2 bg-light border-bottom fw-semibold text-primary">
                  {group.alcance}
                </div>
                <div className="table-responsive">
                  <Table className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '90px' }}>Cant.</th>
                        <th style={{ width: '110px' }}>Unidad</th>
                        <th>Producto</th>
                        <th>Descripción</th>
                        <th>Nota</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item) => {
                        const productName = item.name || item.description;
                        const showDescription =
                          item.name && item.description && item.description !== item.name;
                        return (
                          <tr key={item.id}>
                            <td>
                              {item.quantity ?? (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{item.unit || <span className="text-muted">-</span>}</td>
                            <td>
                              <span className="fw-semibold">{productName}</span>
                              {item.is_inferred && (
                                <Badge bg="warning" text="dark" className="ms-2">
                                  <i className="bi bi-question-circle me-1"></i>
                                  Falta confirmar
                                </Badge>
                              )}
                            </td>
                            <td className="text-muted small">
                              {showDescription ? item.description : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-muted small">{item.note || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      {/* Notes */}
      {rfq.notes && (
        <Card className="shadow-sm mb-4">
          <Card.Header>
            <strong>Notas</strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
              {rfq.notes}
            </p>
          </Card.Body>
        </Card>
      )}
    </Container>
    </DashboardLayout>
  );
};

export default RfqDetailPage;
